import React from 'react';
import styled from '@emotion/styled';
import { Target, Users, Tag } from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation.jsx';

// ─── Styled Components ───────────────────────────────────────────

const Content = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-track { background: rgba(255, 255, 255, 0.05); }
  &::-webkit-scrollbar-thumb { background: rgba(255, 255, 255, 0.2); border-radius: 3px; }
`;

const Stats = styled.div`
  display: flex; gap: 16px; margin-bottom: 20px;
`;
const Stat = styled.div`
  display: flex; flex-direction: column; align-items: center; gap: 4px;
`;
const StatValue = styled.span`
  font-size: 1.4rem; font-weight: 700; color: #9C27B0;
`;
const StatLabel = styled.span`
  font-size: 0.7rem; color: rgba(255, 255, 255, 0.5); text-transform: uppercase;
`;

const PivotCard = styled.div`
  background: ${props => props.$selected ? 'rgba(156, 39, 176, 0.2)' : 'rgba(255, 255, 255, 0.05)'};
  border: 1px solid ${props => props.$selected ? 'rgba(156, 39, 176, 0.5)' : 'rgba(255, 255, 255, 0.1)'};
  border-radius: 10px; padding: 14px; margin-bottom: 10px; cursor: pointer; transition: all 0.2s ease;
  &:hover { background: rgba(156, 39, 176, 0.15); border-color: rgba(156, 39, 176, 0.4); transform: translateX(-2px); }
`;
const PivotCardTitle = styled.div`
  display: flex; align-items: center; gap: 8px; font-weight: 600; color: white; font-size: 0.9rem; margin-bottom: 6px;
`;
const PivotCardMeta = styled.div`
  display: flex; gap: 12px; margin-bottom: 6px;
`;
const PivotCardMetaItem = styled.span`
  display: flex; align-items: center; gap: 4px; font-size: 0.75rem; color: rgba(255, 255, 255, 0.5);
`;
const PivotCardDescription = styled.div`
  font-size: 0.8rem; color: rgba(255, 255, 255, 0.6); line-height: 1.4; margin-bottom: 8px;
`;
const PivotCardScenarios = styled.div`
  display: flex; flex-wrap: wrap; gap: 4px;
`;
const ScenarioBadge = styled.span`
  font-size: 0.65rem; padding: 2px 8px; border-radius: 10px;
  background: rgba(255, 255, 255, 0.08); color: rgba(255, 255, 255, 0.6);
`;

/**
 * PivotPointsDrawerContent — stats + pivot card list for the /pivotpoints drawer.
 * Discovery is the module's admin action in /admin/workshop (pivot_points_v1.discover).
 */
const PivotPointsDrawerContent = ({
  pivotPoints, nodes, scenarios, clusteredNodeIds,
  selectedPivotId, setSelectedPivotId,
}) => {
  const { t } = useTranslation();

  return (
    <Content>
      <Stats>
        <Stat><StatValue>{pivotPoints.length}</StatValue><StatLabel>{t('pivotPoints.drawer.statsPivots')}</StatLabel></Stat>
        <Stat><StatValue>{clusteredNodeIds.size}</StatValue><StatLabel>{t('pivotPoints.drawer.statsClustered')}</StatLabel></Stat>
        <Stat><StatValue>{pivotPoints.filter(p => p.isCrossScenario).length}</StatValue><StatLabel>{t('pivotPoints.drawer.statsCrossScenario')}</StatLabel></Stat>
      </Stats>

      {pivotPoints.map(pivot => {
        const scenarioCounts = {};
        (pivot.memberIds || []).forEach(memberId => {
          const memberNode = nodes.find(n => n.id === memberId);
          if (memberNode?.scenarioId) {
            const scenario = scenarios.find(s => s.id === memberNode.scenarioId);
            const unknown = t('pivotPoints.common.unknown');
            scenarioCounts[scenario?.title || unknown] = (scenarioCounts[scenario?.title || unknown] || 0) + 1;
          }
        });

        return (
          <PivotCard key={pivot.id} $selected={selectedPivotId === pivot.id} onClick={() => setSelectedPivotId(pivot.id)}>
            <PivotCardTitle><Target size={16} style={{ color: '#9C27B0' }} />{pivot.text}</PivotCardTitle>
            <PivotCardMeta>
              <PivotCardMetaItem><Users size={14} />{t('pivotPoints.common.membersCount', { count: pivot.memberCount || pivot.size || 0 })}</PivotCardMetaItem>
              {pivot.confidence && <PivotCardMetaItem><Tag size={14} />{Math.round(pivot.confidence * 100)}%</PivotCardMetaItem>}
            </PivotCardMeta>
            {pivot.description && <PivotCardDescription>{pivot.description.length > 100 ? pivot.description.slice(0, 100) + '...' : pivot.description}</PivotCardDescription>}
            {Object.keys(scenarioCounts).length > 0 && (
              <PivotCardScenarios>
                {Object.entries(scenarioCounts).map(([name, count]) => <ScenarioBadge key={name}>{name} ×{count}</ScenarioBadge>)}
              </PivotCardScenarios>
            )}
          </PivotCard>
        );
      })}

      {pivotPoints.length === 0 && (
        <div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.5)', padding: '20px' }}>
          {t('pivotPoints.drawer.emptyStart').split('\n').map((line, i) => (
            <React.Fragment key={i}>{i > 0 && <br />}{line}</React.Fragment>
          ))}
        </div>
      )}
    </Content>
  );
};

export default PivotPointsDrawerContent;
